import { isProduction } from '../constants';
import { captureFullQueryString, encodeCustomParam } from './queryParamHelper';

export const isValidSessionUUID = (value: string): boolean =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

export const getValidSessionFromURL = (): string | undefined => {
  const sessionParam = new URLSearchParams(window.location.search).get('session');
  return sessionParam && isValidSessionUUID(sessionParam) ? sessionParam : undefined;
};

const hasSessionParam = (queryString: string): boolean =>
  /(?:^|&)session(?:=|&|$)/.test(queryString);

export const filterSessionParam = (queryString: string): string => {
  if (!queryString) return '';
  if (!hasSessionParam(queryString)) return queryString;
  const params = new URLSearchParams(queryString);
  params.delete('session');
  return params.toString();
};

export const isProdTesting = (queryString: string): boolean => {
  if (!queryString) return false;
  const params = new URLSearchParams(queryString);
  return params.get('prodTesting') === 'true';
};

/**
 * Detects the e2eProdTesting flag. Mirrors isProdTesting but with the OPPOSITE
 * intent: the product is already prod and must keep normal prod behavior — this
 * flag only routes Iterable/GA/Salesforce/Slack/Firestore artifacts to QA.
 *
 * Exact-key, case-sensitive matching (capital `P`), so it never collides with
 * the lowercase `prodTesting` matchers and never triggers the prodTesting
 * iframe path (which drops test_mode / swaps the LendAPI read key).
 */
export const isE2EProdTesting = (queryString: string): boolean => {
  if (!queryString) return false;
  const params = new URLSearchParams(queryString);
  return params.get('e2eProdTesting') === 'true';
};

/**
 * Resolves whether GA events will be routed to the QA GTM environment (and thus
 * the QA GA property) rather than prod. Mirrors the `isQA` gate in
 * public/index.html, which loads gtm.js with the QA gtm_auth/gtm_preview tokens
 * under exactly this condition: e2eProdTesting=true OR a non-prod host. Keep in
 * sync with public/index.html.
 */
export const isQAAnalyticsEnvironment = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (isE2EProdTesting(window.location.search)) return true;
  const host = window.location.hostname;
  return ['stage-', 'localhost', 'qa.', '.qa'].some((env) => host.includes(env));
};

/**
 * Resolves whether the Kameleoon SDK is active on the page. public/index.html
 * short-circuits the Kameleoon script load when e2eProdTesting=true, so queued
 * conversions are never delivered. Mirrors that suppression — keep in sync.
 */
export const isKameleoonEnabled = (): boolean => {
  if (typeof window === 'undefined') return false;
  return !isE2EProdTesting(window.location.search);
};

export const buildLendAPIIframeSrc = (productId: string, sessionId?: string): string => {
  const queryString = captureFullQueryString();
  const prodTesting = isProdTesting(queryString);

  let customParamString = '';
  if (!sessionId) {
    // NOTE: `prodTesting` is intentionally LEFT in the custom blob (it used to be
    // stripped here). The LendAPI Full App funnel's webhook is invoked
    // server-to-server BY LendAPI, so the only channel that can tell the prod
    // webhook "this is a QA/e2e run — route Salesforce to QA" is
    // `app_url_params.custom`. Combined/Prequal read `prodTesting` from the
    // webhook body (not custom) and the prequal-field allowlist drops it, so
    // keeping it here is inert for them and for real customer traffic (which
    // never carries the flag). Only `session` is filtered out of custom.
    const filteredQueryString = filterSessionParam(queryString);
    const customParam = filteredQueryString ? encodeCustomParam(filteredQueryString) : '';
    customParamString = customParam ? `&custom=${customParam}` : '';
  }

  const baseProductId = sessionId ? productId.split('_')[0] : productId;

  const basePath = sessionId
    ? `/application/${baseProductId}/${sessionId}/`
    : `/application/product/${productId}/`;

  if (isProduction() || prodTesting) {
    return `https://app.lendapi.com${basePath}?lendAPI=true${customParamString}`;
  }
  return `https://app.lendapi.com${basePath}?lendAPI=true&test_mode=1${customParamString}`;
};
